import { VideoProcessor } from 'twilio-video';

const FRAME_TIMEOUT_MS = 5000;
const BLACK_FRAME_LIMIT = 3;

// Inspect a tiny copy once per second. Comparing input and output avoids treating
// a covered camera or a dark room as a broken effect.
export function measureFrameBrightness(
	source: CanvasImageSource,
): number | undefined {
	try {
		const sample = document.createElement('canvas');
		sample.width = sample.height = 8;
		const context = sample.getContext('2d', { willReadFrequently: true });
		if (!context) return;
		context.drawImage(source, 0, 0, 8, 8);
		const { data } = context.getImageData(0, 0, 8, 8);
		let total = 0;
		for (let i = 0; i < data.length; i += 4) {
			total += Math.max(data[i], data[i + 1], data[i + 2]);
		}
		return total / 64;
	} catch {
		// Some browser frame types cannot be sampled. Processing can still work.
		return undefined;
	}
}

export function isUnexpectedBlackFrame(
	inputBrightness: number | undefined,
	output: HTMLCanvasElement,
) {
	const outputBrightness = measureFrameBrightness(output);
	return (
		inputBrightness !== undefined &&
		inputBrightness > 20 &&
		outputBrightness !== undefined &&
		outputBrightness < 2
	);
}

export default function guardBackgroundProcessor(
	processor: VideoProcessor,
	onFailure: (error: unknown) => void,
	onReady?: () => void,
) {
	let cancelled = false;
	let failed = false;
	let lastCheck = 0;
	let blackFrames = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	const fail = (error: unknown) => {
		if (!cancelled && !failed) {
			failed = true;
			onFailure(error);
		}
	};
	const guarded: VideoProcessor = {
		async processFrame(input, output) {
			if (cancelled || failed) return;
			timer = setTimeout(
				() => fail(new Error('Background frame processing timed out')),
				FRAME_TIMEOUT_MS,
			);
			// A worker transfers ownership of VideoFrame, so sample before processing.
			const shouldCheck = Date.now() - lastCheck >= 1000;
			const inputBrightness = shouldCheck
				? measureFrameBrightness(input as CanvasImageSource)
				: undefined;
			if (shouldCheck) lastCheck = Date.now();
			try {
				await processor.processFrame(input, output);
				if (cancelled || failed) return;
				if (shouldCheck) {
					blackFrames = isUnexpectedBlackFrame(
						inputBrightness,
						output,
					)
						? blackFrames + 1
						: 0;
					if (blackFrames >= BLACK_FRAME_LIMIT)
						fail(
							new Error(
								'Background effect produced black frames',
							),
						);
				}
				if (!failed && blackFrames === 0) onReady?.();
			} catch (error) {
				fail(error);
			} finally {
				clearTimeout(timer);
			}
		},
	};
	return {
		processor: guarded,
		cancel: () => {
			cancelled = true;
			clearTimeout(timer);
		},
	};
}
